import { ScrollView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text, ScreenContainer, Switch } from "@src/components";
import {
  NotificationPreferencesForm,
  NotificationPreferencesFormSkeleton,
  NotificationAccessBanner,
  type NotificationChannel,
} from "@visioni/a3s-notifications/app";
import { useNotificationPreferencesPage } from "@src/features/settings/hooks";
import { ImpersonatedSettingsBanner } from "@src/features/auth/components";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { usePushNotificationSwitch } from "@src/features/notifications";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

/** Push without the feature flag: no switch to register this device, so no column to opt in with either. */
const PUSH_HIDDEN: ReadonlySet<NotificationChannel> = new Set(["push"]);

export default function NotificationPreferencesScreen() {
  const { theme } = useUnistyles();
  const {
    isLoading,
    isRefreshing,
    isError,
    allCameras,
    sites,
    cameras,
    onToggleAllCamerasChannel,
    onToggleSiteChannel,
    onToggleCameraChannel,
    phoneNumber,
    onSavePhoneNumber,
    accessNoticeReason,
  } = useNotificationPreferencesPage();
  const { phoneCountryCode } = useRegionConfig();
  const isPushEnabled = useBooleanFlag(FEATURE_FLAGS.PUSH_NOTIFICATIONS);
  const pushSwitch = usePushNotificationSwitch({ enabled: isPushEnabled });

  return (
    <ScreenContainer edges={["bottom"]}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: theme.spacing["5xl"], paddingTop: theme.spacing["4xl"] }}
      >
        {isLoading ?
          <NotificationPreferencesFormSkeleton hiddenChannels={isPushEnabled ? undefined : PUSH_HIDDEN} />
        : isError ?
          <View py="4xl" alignItems="center">
            <Text textStyle="sm/regular" color="textMuted">
              {"Couldn't load your notification preferences. Please try again."}
            </Text>
          </View>
        : <NotificationPreferencesForm
            allCameras={allCameras}
            onToggleAllCamerasChannel={onToggleAllCamerasChannel}
            sites={sites}
            onToggleSiteChannel={onToggleSiteChannel}
            cameras={cameras}
            onToggleCameraChannel={onToggleCameraChannel}
            phoneNumber={phoneNumber}
            defaultPhoneCountry={phoneCountryCode}
            onSavePhoneNumber={onSavePhoneNumber}
            disabled={isRefreshing}
            accessNotice={
              accessNoticeReason ?
                <NotificationAccessBanner reason={accessNoticeReason} />
              : <ImpersonatedSettingsBanner settings="notification preferences" />
            }
            pushSwitch={
              isPushEnabled ?
                {
                  ...pushSwitch,
                  renderSwitch: ({ active, onToggle, disabled }) => (
                    <Switch active={active} onToggle={onToggle} disabled={disabled} size="medium" />
                  ),
                }
              : undefined
            }
            hiddenChannels={isPushEnabled ? undefined : PUSH_HIDDEN}
          />
        }
      </ScrollView>
    </ScreenContainer>
  );
}
